import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { IonToast } from '@ionic/react';
import toastServiceInstance from '../services/toastService';

interface ToastOptions {
  message: string;
  color?: 'success' | 'danger' | 'warning' | 'primary' | 'secondary' | 'tertiary';
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
  buttons?: Array<{
    text: string;
    role?: string;
    handler?: () => void;
  }>;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [toastOptions, setToastOptions] = useState<ToastOptions>({
    message: '',
    color: 'primary',
    duration: 3000,
    position: 'top'
  });

  const showToast = useCallback((options: ToastOptions) => {
    setToastOptions({
      color: 'primary',
      duration: 3000,
      position: 'top',
      ...options
    });
    setIsOpen(true);
  }, []);

  // Initialize the toast service singleton with our showToast function
  useEffect(() => {
    toastServiceInstance.initialize(showToast);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <IonToast
        isOpen={isOpen}
        message={toastOptions.message}
        duration={toastOptions.duration}
        position={toastOptions.position}
        color={toastOptions.color}
        buttons={toastOptions.buttons || [
          {
            text: 'X',
            role: 'cancel'
          }
        ]}
        onDidDismiss={() => setIsOpen(false)}
      />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
